import { useCallback, useEffect, useState } from "react"
import { Group } from "../../../../types/types"
import { Workflow } from "../../../../types/starAPIResponseTypes"

import { FileExplorerDataProps } from "../../WorkflowFileExplorer.types"
import FolderIcon from "../../../../media/FolderIcon"
import { addGroup, deleteGroup, getGroups, getGroupsWithParent } from "../../../../service/groupService"
import { getWorkflowsForGroup, removeWorkflowFromGroup } from "../../../../service/workflowService"
import { useNavigate } from "react-router-dom"
import MyFilesSection from "./MyFilesSection"
import SkeletonLoadingView from "../../../../components/SkeletonLoadingView/SkeletonLoadingView"
import { showErrorToast, showLoadingToast, showSuccessToast } from "../../../../utils/utils"
import { Id, toast } from "react-toastify"

const useMyFileSection = (props: FileExplorerDataProps) => {

  const { onError, searchQuery } = props
  const [workflows, setWorkflows] = useState<Workflow[]>([])
  const [groups, setGroups] = useState<Group[]>([])
  const [parentGroups, setParentGroups] = useState<Group[]>([])
  const [showAddGroup, setShowAddGroup] = useState<boolean>(false)
  const [newGroup, setNewGroup] = useState<string>("")
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error|undefined>(undefined)
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow|undefined>(undefined)
  const [selectedGroup, setSelectedGroup] = useState<Group|undefined>(undefined)
  const [showMoveWorkflow, setShowMoveWorkflow] = useState<boolean>(false)
  const [showMoveGroup, setShowMoveGroup] = useState<boolean>(false)
  const [loadingToastId, setLoadingToastId] = useState<Id|undefined>(undefined)
  const navigate = useNavigate()

  useEffect(() => {
    if (!isLoading && loadingToastId !== undefined) {
      toast.done(loadingToastId)
    }
  }, [isLoading, loadingToastId])

  /**
   * Handle http response errors and update state to reflect
   * 
   * @param error that occured.
   */
  const handleError = useCallback((error: Error) => {
    onError(error)
    setIsLoading(false)
    setShowAddGroup(false)
    setError(error)
  }, [onError])

  /**
   * Gets all workflows for the current group.
   */
  const fetchGroupWorkflows = useCallback(async () => {
    setIsLoading(true)
    if (parentGroups.length === 0) {
      setWorkflows([])
      return
    }
    const parentGroup = parentGroups[parentGroups.length-1]
    const response = await getWorkflowsForGroup(parentGroup.id)
    
    if (response instanceof Error) {
      handleError(response)
      return
    }
    const workflows = response as Workflow[]
    setWorkflows(workflows)
    setIsLoading(false)
  }, [parentGroups, handleError])

  /**
   * Gets all groups for the current group.
   */
  const fetchGroups = useCallback(async () => {
    setIsLoading(true)
    let response;
    if (parentGroups.length === 0) {
      response = await getGroups()                 
    } else {
      response = await getGroupsWithParent(parentGroups[parentGroups.length-1].id)
    }

    if (response instanceof Error) {
      handleError(response)
      return
    }

    setGroups(response as Group[])
    setIsLoading(false)
  }, [parentGroups, handleError])

  /**
   * Refresh list of rendered groups and workflows.
   */
  const onRefresh = useCallback(() => {
    fetchGroups()
    fetchGroupWorkflows()
    setError(undefined)
  }, [fetchGroups, fetchGroupWorkflows])

  /**
   * Fetches groups and workflows when the component mounts.
   */
  useEffect(() => {
    onRefresh()
  }, [onRefresh])

  /**
   * Navigates to the workflow analysis page
   * @param workflow to open.
   */
  const onWorkflowClick = (workflow: Workflow) => {
    window.open(`/workflow/${workflow.workflow_id}`, "_blank")
  }

  /**
   * Creates a new group and adds it to the current group.
   */
  const onAddGroup = async () => {
    if (newGroup.trim() === "") {
      showErrorToast("File name cannot be empty.")
      return
    }
    setIsLoading(true)
    setLoadingToastId(showLoadingToast(`Creating ${newGroup}.`))
    setShowAddGroup(false)
    const res = await addGroup(newGroup, parentGroups[parentGroups.length-1]?.id)
    setIsLoading(false)
    if (res instanceof Error) {
      handleError(res)
      return
    }
    setParentGroups([...parentGroups, ...[res as Group]])
    setShowAddGroup(false)
    showSuccessToast(`${newGroup} created successfully.`)
    setNewGroup("")
    onRefresh()
  }

  /**
   * Deletes the selected group.
   */
  const onDeleteGroup = async () => {
    if (selectedGroup === undefined) {
      showErrorToast("Failed to delete group.")
      return
    }
    
    setLoadingToastId(showLoadingToast(`Deleting ${selectedGroup}.`))
    setIsLoading(true)
    const res = await deleteGroup(selectedGroup.id)
    setIsLoading(false)
    if (res instanceof Error) {
      handleError(res)
      return
    }
    showSuccessToast(`${selectedGroup.name} deleted successfully.`)
    onRefresh()
  }

  /**
   * Sets selected workflow to highlight.
   * 
   * @param workflow to select.
   */
  const onSelectWorkflow = (workflow: Workflow) => {
    setSelectedWorkflow(workflow)
    setSelectedGroup(undefined)
  }

  /**
   * Sets the selected group to highlight.
   * 
   * @param group to select.
   */
  const onClickGroup = (group: Group) => {
    setSelectedGroup(group)
    setSelectedWorkflow(undefined)
  }

  /**
   * Removes a workflow from a group.
   */
  const onRemoveWorkflowFromGroup = async () => {
    if (selectedWorkflow === undefined || parentGroups.length === 0) {
      showErrorToast("Failed to remove workflow from group.")
      return
    }
    
    setIsLoading(true)
    setLoadingToastId(showLoadingToast(`Removing workflow from group.`))
    const res = await removeWorkflowFromGroup(selectedWorkflow.workflow_id, parentGroups[parentGroups.length-1].id)
    setIsLoading(false)
    if (res instanceof Error) {
      handleError(res)
      return
    }
    onRefresh()
  }

  const onMoveWorkflowGroup = () => {
    setShowMoveWorkflow(false)
    setShowMoveGroup(false)
    onRefresh()
  }

  return [{
    fileExplorerOption: {
      option: {
        icon: <FolderIcon />,
        label: "My Files",
        onAdd: () => setShowAddGroup(true)
      },
      component: <MyFilesSection 
                    workflows={workflows.filter(workflow => workflow.asset_id.toLowerCase().includes(searchQuery.toLowerCase()))}
                    onOpenWorkflow={onWorkflowClick}
                    onDeleteWorkflow={onRemoveWorkflowFromGroup}
                    onMoveWorkflow={() => setShowMoveWorkflow(true)}
                    onAddWorkflow={() => {}}
                    onHideWorkflow={() => {}}
                    showAddWorkflow={false}
                    selectedWorkflow={selectedWorkflow}
                    onSelectWorkflow={onSelectWorkflow}
                    showMoveWorkflow={showMoveWorkflow}
                    onHideMoveWorkflow={() => setShowMoveWorkflow(false)}

                    groups={groups.filter(group => group.name.toLowerCase().includes(searchQuery.toLowerCase()))}   
                    selectedGroup={selectedGroup}
                    onSelectGroup={onClickGroup}
                    parentGroups={parentGroups}
                    onSelectParentGroup={ (index: number) => setParentGroups(parentGroups.slice(0, index)) }
                    onDeleteGroup={onDeleteGroup}
                    onMoveGroup={() => setShowMoveGroup(true)}
                    onAddGroup={onAddGroup}
                    onHideAddGroup={() => setShowAddGroup(false)}
                    onOpenGroup={ (group: Group) => setParentGroups([...parentGroups, ...[group]])}
                    showAddGroup={showAddGroup}
                    newGroupInput={{ value: newGroup, onChange: setNewGroup, placeholder: "Group name" }}
                    showMoveGroup={showMoveGroup}
                    onHideMoveGroup={() => setShowMoveGroup(false)}
                    error={error}
                    onError={handleError}
                    onMoveWorkflowGroup={onMoveWorkflowGroup}
                  />,
      onRefresh: onRefresh,
      canLoadMore: false,
      filterOptions: <p>Filter</p>,
      sortOptions: <p>Sort</p>,
      isLoading: isLoading,
      loadingView: <>
        <div className="myfiles-header-container content-loading-view">
          <p className="workflows-result-header">NAME</p>
          <p className="workflows-result-header">DATE</p>
          <p className="workflows-result-header"></p>
          {[...Array(5)].map((_) => (
            <>
              <SkeletonLoadingView />
              <SkeletonLoadingView style={{gridColumn: "span 2"}} />
            </>
          ))}
        </div>
      </>
    }
  }]
}

export default useMyFileSection